import { useEffect } from 'react'
import { Link, useNavigate } from "react-router-dom"
import { useForm, FormProvider } from 'react-hook-form'
import { useTheme } from '@mui/material/styles'
import { Box, Stack, useMediaQuery } from '@mui/material'
import format from 'date-fns/format'
import { createStore, Provider } from 'jotai'
import { H1, H5, Body } from 'components/styledFonts'
import { LinkButtonLarge, LinkButtonMiddle, BlueContainedButton, BlueOutlinedButton } from 'components/styledButtons'
import Block from 'components/Block'
import PRBarChart from 'pages/pranalysis/PRBarChart'
import PRRadarChart from './PRRadarChart'
import PRQuestions from './PRQuestions'
import PageTitleAndButtons from 'components/PageTitleAndButtons'
import TypeTab from './TypeTab'
import { usePRAnalysisAll } from 'hooks/pranalysis'
import { usePranalysisMutaion } from 'hooks/mutation/pranalysis'
import { useLoginUser } from 'hooks/loginuser'
import { useMyBeforeUnload } from 'hooks/authentication'
import Warnning from './Warnning'
import Blocker from 'components/Blocker'


export const defaultValues = {
    下書き : false
}

const PRAnalysis = () => {
    const questionUseForm = useForm({ defaultValues : defaultValues })
    const theme = useTheme()
    const notNarrow = useMediaQuery(theme.breakpoints.between('mobile', 'md'))
    const navigate = useNavigate()
    const { mutate : mutatePranalysis } = usePranalysisMutaion()
    const { data : loginUser } = useLoginUser()
    const { data, isFetching } = usePRAnalysisAll()
    const store = createStore()
    useMyBeforeUnload()
    
    useEffect(() => {
        if (data && Object.keys(data.master).length === 0){
            navigate("/pranalysis/history")
        }
    }, [data, navigate])

    if (!data) return null

    const handleSubmitAnswer = (answerInfo) => {
        if (!answerInfo.下書き
            && !window.confirm('回答完了すると、経営者・担当者ともに編集できなくなります。よろしいですか？')) {
            return
        }
        const latestPrData = data.pranalysis.at(-1)

        if (!data.master.レコード番号) { // 一時保存レコードの更新
            mutatePranalysis({
                PR活動診断日 : format(new Date(), 'yyyy-MM-dd'),
                下書き : answerInfo.下書き,
                質問グループ: { question : latestPrData, answer : answerInfo },
                id : latestPrData.$id,
                revision : latestPrData.$revision
            })
        } else { // 新規レコード登録
            mutatePranalysis({
                PR活動診断日 : format(new Date(), 'yyyy-MM-dd'),
                企業ID : loginUser?.企業ID,
                活動診断テンプレート : data.master.レコード番号,
                診断回 : latestPrData?.診断回 ? latestPrData.診断回 + 1 : 1,
                下書き : answerInfo.下書き,
                特殊質問グループ設定 : data.master.特殊質問グループ設定,
                質問グループ: { question : data.master, answer : answerInfo }
            })
        }  
    }

    return (
        <Stack
            gap={2}
        >
            <Stack
                sx={{
                    flexDirection : { sm : "row", xs : "column" },
                    pb : { sm : 0, xs : 2 },
                    justifyContent : "space-between",
                    gap : 2,
                }}
            >
                <Stack
                    gap={1}
                >
                    <H1>広報活動診断</H1>
                    <Stack>
                        <H5>■注意事項■</H5>
                        <Body
                            sx={{ whiteSpace : "pre-wrap" }}
                        >
                            {data.master.注意事項}
                        </Body>
                    </Stack>
                </Stack>
                <Stack
                    alignItems="center"
                    flexShrink={0}
                    gap={0.5}
                >
                    <H5>ご不明点等ございましたら、こちらから</H5>
                    <LinkButtonLarge
                        component={Link}
                        to="/messages/support"
                    >
                        お問い合わせ
                    </LinkButtonLarge>
                </Stack>
            </Stack>
            {data.hasCompletedAnswer &&
            <Block>
                <Provider store={store}>
                    <Stack
                        gap={3}
                    >
                        <TypeTab />
                        <PageTitleAndButtons
                            title="前回の結果"
                            buttons={[
                                <BlueOutlinedButton
                                    label="過去のご回答"
                                    component={Link}
                                    target="_blank"
                                    to="/pranalysis/history"
                                />]}
                        />
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Box
                                width="90%"
                                sx={{
                                    display : { md : "block", xs : "none" },
                                    position: "relative",
                                }}
                            >
                                <PRRadarChart targetPrIndex={data.pranalysis.filter(p => !p.下書き).length} />
                            </Box>
                            <Stack
                                sx={{
                                    width : { md : 350, xs : "100%" },
                                    rowGap: 3,
                                }}
                            >
                                <PRBarChart type="経営者" narrow={!notNarrow} />
                                <PRBarChart type="担当者" narrow={!notNarrow} />
                            </Stack>
                        </Stack>
                    </Stack>
                </Provider>
            </Block>
            }
            <FormProvider {...questionUseForm}>
                <Stack
                    gap={3}
                    component="form"
                    onSubmit={questionUseForm.handleSubmit(handleSubmitAnswer)}
                >
                    <PRQuestions type="経営者" />
                    <PRQuestions type="担当者" />
                    <Warnning />
                    <Stack
                        direction="row"
                        justifyContent="center"
                        sx={{ columnGap : { sm : 8, xs : 4 } }}
                    >
                        <LinkButtonMiddle
                            type="submit"
                            disabled={isFetching}
                            sx={{ minWidth : 150 }}
                            onClick={() => questionUseForm.setValue('下書き', true)}
                        >
                            一時保存
                        </LinkButtonMiddle>
                        <BlueContainedButton
                            label="回答完了"
                            type="submit"
                            disabled={isFetching}
                            sx={{ minWidth : 150 }}
                            onClick={() => questionUseForm.setValue('下書き', false)}
                        />
                    </Stack>
                </Stack>
            </FormProvider>
            <Blocker />
        </Stack>
    )
}

export default PRAnalysis
